/**
 * Single source of truth for the Studio Open Day.
 *
 * The banner, the /open-day page, the schema.org Event mirror and the RSVP
 * widget all read from here, so the date can never drift between them.
 *
 * To run another open day: change these constants. To retire the current one:
 * do nothing - every surface self-retires once `END` passes (see
 * `isOpenDayOver`), so there is no dead promo left on the site.
 */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

/** Absolute origin: canonical, schema.org and calendar links all need one. */
import { SITE_ORIGIN } from './site';

export { SITE_ORIGIN };

export const OPEN_DAY_TIMEZONE = 'Australia/Adelaide';

/** Local Adelaide wall-clock times. Must match the Eventbrite listing. */
export const OPEN_DAY_START = '2026-09-12 10:00:00';
export const OPEN_DAY_END = '2026-09-12 14:00:00';

/** Calendar date alone, for <time dateTime>. */
export const OPEN_DAY_DATE = OPEN_DAY_START.slice(0, 10);

export const OPEN_DAY = {
  name: 'Studio Open Day',
  /** Human date, used in copy. Kept beside the machine dates so they cannot drift. */
  dayLabel: 'Saturday 12 September',
  timeLabel: '10am - 2pm',
  venue: 'Music Eternal',
  streetAddress: '687A Brighton Road',
  suburb: 'Seacliff',
  region: 'SA',
  postcode: '5049',
  path: '/open-day',
  eventbriteId: '1993473271217',
  eventbriteUrl:
    'https://www.eventbrite.com.au/e/studio-open-day-tickets-1993473271217',
} as const;

export const OPEN_DAY_ADDRESS = `${OPEN_DAY.streetAddress}, ${OPEN_DAY.suburb} ${OPEN_DAY.region} ${OPEN_DAY.postcode}`;

export const OPEN_DAY_MAP_URL = `https://maps.google.com/?q=${encodeURIComponent(
  `${OPEN_DAY.venue}, ${OPEN_DAY_ADDRESS}`,
)}`;

/** Milliseconds since epoch, resolved from Adelaide wall-clock time. */
export const openDayStartMs = (): number =>
  dayjs.tz(OPEN_DAY_START, OPEN_DAY_TIMEZONE).valueOf();

export const openDayEndMs = (): number =>
  dayjs.tz(OPEN_DAY_END, OPEN_DAY_TIMEZONE).valueOf();

/** True once the doors have closed. Every open-day surface hides itself after this. */
export const isOpenDayOver = (now: number = Date.now()): boolean =>
  now >= openDayEndMs();

/** True while the doors are open, so copy can switch to "we're open right now". */
export const isOpenDayLive = (now: number = Date.now()): boolean =>
  now >= openDayStartMs() && now < openDayEndMs();

/** ISO 8601 with offset, for schema.org and <time dateTime>. */
export const openDayStartIso = (): string =>
  dayjs.tz(OPEN_DAY_START, OPEN_DAY_TIMEZONE).format();

export const openDayEndIso = (): string =>
  dayjs.tz(OPEN_DAY_END, OPEN_DAY_TIMEZONE).format();

/** Compact UTC stamp (20260912T003000Z), the format both Google and iCalendar want. */
const toCalendarStamp = (ms: number): string =>
  new Date(ms)
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '');

export const openDayGoogleCalendarUrl = (): string => {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `Music Eternal ${OPEN_DAY.name}`,
    dates: `${toCalendarStamp(openDayStartMs())}/${toCalendarStamp(openDayEndMs())}`,
    details: `Drop in any time between ${OPEN_DAY.timeLabel}. Try the instruments, meet the teachers, look around. ${SITE_ORIGIN}${OPEN_DAY.path}`,
    location: `${OPEN_DAY.venue}, ${OPEN_DAY_ADDRESS}`,
    ctz: OPEN_DAY_TIMEZONE,
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/** Outlook Web takes ISO local times plus an explicit offset. */
export const openDayOutlookUrl = (): string => {
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: `Music Eternal ${OPEN_DAY.name}`,
    startdt: openDayStartIso(),
    enddt: openDayEndIso(),
    location: `${OPEN_DAY.venue}, ${OPEN_DAY_ADDRESS}`,
    body: `Drop in any time between ${OPEN_DAY.timeLabel}. ${SITE_ORIGIN}${OPEN_DAY.path}`,
  });

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

/**
 * What is out on the day, set as a lineup. Also the source of the count shown
 * in the facts strip, so the two can never disagree. "Keys" is deliberately
 * absent: it is the same instrument as Piano and padded the count.
 */
export const OPEN_DAY_LINEUP = [
  'Guitars',
  'Drums',
  'Piano',
  'Violin',
  'Cello',
  'Bass',
  'Ukulele',
  'Flute',
  'Clarinet',
  'Microphones',
] as const;

/** Hour ticks for the arrival ruler: the event is a window, not a start time. */
export const OPEN_DAY_HOUR_TICKS = ['10am', '11', '12', '1', '2pm'] as const;
